import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  MapPin, Plus, Search, Edit, Trash2, Eye, X,
  Package, CheckCircle2, Clock, DollarSign, Tag, Truck, Hash
} from 'lucide-react';

interface Zone {
  id: number;
  name: string;
  code?: string;
  description?: string;
  type?: string;
  basePrice?: number;
  pricePerKg?: number;
  minPrice?: number;
  maxPrice?: number;
  minWeight?: number;
  maxWeight?: number;
  estimatedDeliveryHours?: number;
  isActive: boolean;
  priority?: number;
  createdAt?: string;
  zoneCities?: any[];
}

export default function ZonesPage() {
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [form, setForm] = useState({
    name: '', code: '', description: '', type: 'Local',
    basePrice: '', pricePerKg: '', minPrice: '', maxPrice: '',
    estimatedDeliveryHours: '', isActive: true
  });

  useEffect(() => { loadZones(); }, []);

  const loadZones = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/zones');
      let zonesData = [];
      
      if (response.data?.success) {
        // API returns { success: true, data: { items: [...] } }
        zonesData = response.data.data?.items || [];
      } else if (Array.isArray(response.data)) {
        // API returns array directly
        zonesData = response.data;
      } else if (response.data?.data) {
        zonesData = response.data.data;
      }
      
      setZones(zonesData);
    } catch (error) {
      console.error('Error loading zones:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingZone(null);
    setForm({
      name: '', code: '', description: '', type: 'Local',
      basePrice: '', pricePerKg: '', minPrice: '', maxPrice: '',
      estimatedDeliveryHours: '', isActive: true
    });
    setShowModal(true);
  };

  const openEditModal = (zone: Zone) => {
    setEditingZone(zone);
    setForm({
      name: zone.name || '',
      code: zone.code || '',
      description: zone.description || '',
      type: zone.type || 'Local',
      basePrice: zone.basePrice ? zone.basePrice.toString() : '',
      pricePerKg: zone.pricePerKg ? zone.pricePerKg.toString() : '',
      minPrice: zone.minPrice ? zone.minPrice.toString() : '',
      maxPrice: zone.maxPrice ? zone.maxPrice.toString() : '',
      estimatedDeliveryHours: zone.estimatedDeliveryHours ? zone.estimatedDeliveryHours.toString() : '',
      isActive: zone.isActive !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        code: form.code,
        description: form.description,
        type: form.type,
        basePrice: form.basePrice ? parseFloat(form.basePrice) : 0,
        pricePerKg: form.pricePerKg ? parseFloat(form.pricePerKg) : 0,
        minPrice: form.minPrice ? parseFloat(form.minPrice) : 0,
        maxPrice: form.maxPrice ? parseFloat(form.maxPrice) : 0,
        estimatedDeliveryHours: form.estimatedDeliveryHours ? parseInt(form.estimatedDeliveryHours) : 0,
        isActive: form.isActive
      };
      if (editingZone) {
        await api.put('/api/zones/' + editingZone.id, payload);
        alert('تم تحديث بيانات المنطقة بنجاح');
      } else {
        await api.post('/api/zones', payload);
        alert('تم إضافة المنطقة بنجاح');
      }
      setShowModal(false);
      loadZones();
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حفظ بيانات المنطقة');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المنطقة؟')) return;
    try {
      await api.delete('/api/zones/' + id);
      loadZones();
      alert('تم حذف المنطقة بنجاح');
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حذف المنطقة');
    }
  };

  const filteredZones = Array.isArray(zones) ? zones.filter(z =>
    (z.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (z.code || '').toLowerCase().includes(search.toLowerCase()) ||
    (z.type || '').toLowerCase().includes(search.toLowerCase())
  ) : [];

  const stats = {
    total: zones.length,
    active: zones.filter(z => z.isActive !== false).length,
    avgBasePrice: zones.length > 0
      ? (zones.reduce((sum, z) => sum + (z.basePrice || 0), 0) / zones.length).toFixed(0)
      : 0,
    totalCities: zones.reduce((sum, z) => sum + (z.zoneCities?.length || 0), 0)
  };

  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition';

  const getTypeStyle = (type: string) => {
    switch ((type || '').toLowerCase()) {
      case 'local': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'regional': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'national': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'international': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getTypeLabel = (type: string) => {
    switch ((type || '').toLowerCase()) {
      case 'local': return 'محلي';
      case 'regional': return 'إقليمي';
      case 'national': return 'وطني';
      case 'international': return 'دولي';
      default: return type || '-';
    }
  };

  return (
    <div className='space-y-6' dir='rtl'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 flex items-center gap-3'>
            <div className='w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg'>
              <MapPin className='w-6 h-6 text-white' />
            </div>
            <div>
              <span>إدارة المناطق</span>
              <p className='text-sm text-gray-500 font-normal mt-1'>إدارة المناطق الجغرافية وأسعار التوصيل</p>
            </div>
          </h1>
        </div>
        <Button
          onClick={openCreateModal}
          className='bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg'
        >
          <Plus className='w-5 h-5 ml-2' />
          <span>إضافة منطقة جديدة</span>
        </Button>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <MapPin className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي المناطق</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.total}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center'>
                <CheckCircle2 className='w-5 h-5 text-green-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>المناطق النشطة</p>
            <p className='text-2xl font-bold text-green-700'>{stats.active}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center'>
                <DollarSign className='w-5 h-5 text-blue-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>متوسط السعر الأساسي</p>
            <p className='text-2xl font-bold text-blue-700'>{Number(stats.avgBasePrice).toLocaleString()} ج.م</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <Package className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي المدن</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.totalCities}</p>
          </CardContent>
        </Card>
      </div>

      <Card className='border-0 shadow-lg'>
        <div className='p-6'>
          <div className='flex flex-col md:flex-row md:items-center gap-4 mb-6'>
            <div className='flex-1 relative'>
              <Search className='absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
              <input
                type='text'
                placeholder='بحث باسم المنطقة، الكود، أو النوع...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className='w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition'
              />
            </div>
          </div>
          {loading ? (
            <div className='p-12 text-center'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto'></div>
              <p className='text-gray-500 mt-4'>جاري تحميل المناطق...</p>
            </div>
          ) : filteredZones.length === 0 ? (
            <div className='p-12 text-center'>
              <MapPin className='w-16 h-16 text-gray-300 mx-auto mb-4' />
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>لا توجد مناطق</h3>
              <p className='text-gray-500'>ابدأ بإضافة منطقتك الأولى</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-50 border-b border-gray-100'>
                  <tr>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>اسم المنطقة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الكود</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>النوع</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>السعر الأساسي</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>سعر الكيلو</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>مدة التوصيل</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>المدن</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الحالة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredZones.map((zone) => (
                    <tr key={zone.id} className='border-b border-gray-50 hover:bg-gray-50/50 transition'>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-3'>
                          <div className='w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white'>
                            <MapPin className='w-5 h-5' />
                          </div>
                          <div>
                            <p className='font-semibold text-gray-900'>{zone.name}</p>
                            {zone.description && <p className='text-xs text-gray-500'>{zone.description}</p>}
                          </div>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-mono text-gray-700'>{zone.code || '-'}</span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className={'px-3 py-1 rounded-full text-xs font-semibold border ' + getTypeStyle(zone.type || '')}>
                          {getTypeLabel(zone.type || '')}
                        </span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-bold text-orange-600'>{(zone.basePrice || 0).toLocaleString()} ج.م</span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-semibold text-gray-900'>{(zone.pricePerKg || 0).toLocaleString()} ج.م</span>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-1 text-gray-700'>
                          <Clock className='w-4 h-4 text-gray-400' />
                          <span>{zone.estimatedDeliveryHours || 0}h</span>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-semibold text-blue-600'>{zone.zoneCities?.length || 0}</span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className={'px-3 py-1 rounded-full text-xs font-semibold ' + (
                          zone.isActive !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                        )}>
                          {zone.isActive !== false ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-1'>
                          <button
                            onClick={() => { setSelectedZone(zone); setShowViewModal(true); }}
                            className='p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition'
                            title='عرض'
                          >
                            <Eye className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => openEditModal(zone)}
                            className='p-2 hover:bg-blue-50 rounded-lg text-gray-500 hover:text-blue-600 transition'
                            title='تعديل'
                          >
                            <Edit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(zone.id)}
                            className='p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition'
                            title='حذف'
                          >
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-3xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>
                  {editingZone ? 'تعديل بيانات المنطقة' : 'إضافة منطقة جديدة'}
                </h2>
                <button onClick={() => setShowModal(false)} className='p-2 hover:bg-gray-100 rounded-lg transition'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label>اسم المنطقة *</Label>
                    <Input
                      value={form.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
                      required
                      className={inputClass}
                      placeholder='أدخل اسم المنطقة'
                    />
                  </div>
                  <div>
                    <Label>كود المنطقة</Label>
                    <Input
                      value={form.code}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, code: e.target.value })}
                      className={inputClass}
                      placeholder='CAIRO_METRO'
                    />
                  </div>
                  <div>
                    <Label>نوع المنطقة</Label>
                    <select
                      value={form.type}
                      onChange={(e) => setForm({ ...form, type: e.target.value })}
                      className={inputClass}
                    >
                      <option value='Local'>محلي</option>
                      <option value='Regional'>إقليمي</option>
                      <option value='National'>وطني</option>
                      <option value='International'>دولي</option>
                    </select>
                  </div>
                  <div>
                    <Label>مدة التوصيل (ساعة)</Label>
                    <Input
                      type='number'
                      value={form.estimatedDeliveryHours}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, estimatedDeliveryHours: e.target.value })}
                      className={inputClass}
                      placeholder='24'
                    />
                  </div>
                  <div>
                    <Label>السعر الأساسي (ج.م)</Label>
                    <Input
                      type='number'
                      step='0.01'
                      value={form.basePrice}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, basePrice: e.target.value })}
                      className={inputClass}
                      placeholder='0.00'
                    />
                  </div>
                  <div>
                    <Label>سعر الكيلو (ج.م)</Label>
                    <Input
                      type='number'
                      step='0.01'
                      value={form.pricePerKg}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, pricePerKg: e.target.value })}
                      className={inputClass}
                      placeholder='0.00'
                    />
                  </div>
                  <div>
                    <Label>الحد الأدنى للسعر (ج.م)</Label>
                    <Input
                      type='number'
                      step='0.01'
                      value={form.minPrice}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, minPrice: e.target.value })}
                      className={inputClass}
                      placeholder='0.00'
                    />
                  </div>
                  <div>
                    <Label>الحد الأقصى للسعر (ج.م)</Label>
                    <Input
                      type='number'
                      step='0.01'
                      value={form.maxPrice}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, maxPrice: e.target.value })}
                      className={inputClass}
                      placeholder='0.00'
                    />
                  </div>
                  <div className='md:col-span-2'>
                    <Label>الوصف</Label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className={inputClass}
                      rows={2}
                      placeholder='وصف اختياري للمنطقة...'
                    />
                  </div>
                  <div className='flex items-center gap-2 md:col-span-2'>
                    <input
                      type='checkbox'
                      id='isActive'
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      className='w-4 h-4'
                    />
                    <label htmlFor='isActive' className='text-sm font-medium'>منطقة نشطة</label>
                  </div>
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button type='button' variant='outline' onClick={() => setShowModal(false)} className='flex-1'>إلغاء</Button>
                  <Button type='submit' className='flex-1 bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'>
                    {editingZone ? 'تحديث البيانات' : 'إضافة المنطقة'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {showViewModal && selectedZone && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-3xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>تفاصيل المنطقة</h2>
                <button onClick={() => setShowViewModal(false)} className='p-2 hover:bg-gray-100 rounded-lg'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <div className='space-y-4'>
                <div className='flex items-center gap-4 pb-4 border-b'>
                  <div className='w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white'>
                    <MapPin className='w-8 h-8' />
                  </div>
                  <div>
                    <h3 className='text-xl font-bold'>{selectedZone.name}</h3>
                    <p className='text-sm text-gray-500'>
                      {selectedZone.code} - {getTypeLabel(selectedZone.type || '')}
                    </p>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_كود المنطقة</p>
                    <p className='font-semibold font-mono'>{selectedZone.code || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_نوع المنطقة</p>
                    <span className={'px-3 py-1 rounded-full text-xs font-semibold border ' + getTypeStyle(selectedZone.type || '')}>
                      {getTypeLabel(selectedZone.type || '')}
                    </span>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_السعر الأساسي (ج.م)</p>
                    <p className='font-semibold text-orange-600 text-xl'>{(selectedZone.basePrice || 0).toLocaleString()} ج.م</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_سعر الكيلو (ج.م)</p>
                    <p className='font-semibold text-gray-900'>{(selectedZone.pricePerKg || 0).toLocaleString()} ج.م</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_الحد الأدنى للسعر (ج.م)</p>
                    <p className='font-semibold'>{(selectedZone.minPrice || 0).toLocaleString()} ج.م</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_الحد الأقصى للسعر (ج.م)</p>
                    <p className='font-semibold'>{(selectedZone.maxPrice || 0).toLocaleString()} ج.م</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_مدة التوصيل (ساعة)</p>
                    <p className='font-semibold'>{selectedZone.estimatedDeliveryHours || 0}h</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>الحالة</p>
                    <span className={'px-3 py-1 rounded-full text-xs font-semibold ' + (
                      selectedZone.isActive !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                    )}>
                      {selectedZone.isActive !== false ? 'نشط' : 'غير نشط'}
                    </span>
                  </div>
                  {selectedZone.description && (
                    <div className='col-span-2'>
                      <p className='text-sm text-gray-500'>VIEW_الوصف</p>
                      <p className='font-semibold'>{selectedZone.description}</p>
                    </div>
                  )}
                  {selectedZone.zoneCities && selectedZone.zoneCities.length > 0 && (
                    <div className='col-span-2'>
                      <p className='text-sm text-gray-500 mb-2'>المدن التابعة ({selectedZone.zoneCities.length})</p>
                      <div className='flex flex-wrap gap-2'>
                        {selectedZone.zoneCities.map((zc: any, i: number) => (
                          <span key={i} className='px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-semibold'>
                            {zc.city?.name || zc.cityName || '-'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button onClick={() => { setShowViewModal(false); openEditModal(selectedZone); }} className='flex-1 bg-orange-500 hover:bg-orange-600'>
                    <Edit className='w-4 h-4 ml-2' />
                    تعديل البيانات
                  </Button>
                  <Button onClick={() => setShowViewModal(false)} variant='outline' className='flex-1'>إغلاق</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
